import {
    useLayoutEffect,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    type LayoutChangeEvent,
    type LayoutRectangle,
    type ViewStyle,
    ScrollView,
    StyleSheet,
    Platform,
    View
} from "react-native";
import type ITabsListProps from "./type";
import type {
    TabsListWebPropsType,
    TabsScrollState
} from "./type";
import stylesheet, {
    getTabsListFadeColor,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useStableCallback
} from "../../../../helpers/interaction";
import {
    useMotionTiming
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    TabsPartContentProps,
    TabsListContextType,
    TabsScrollMetrics,
    TabsSlideType
} from "../../type";
import {
    TABS_MEASURE_EPSILON,
    TABS_SCROLL_EPSILON,
    getTabsTriggerItems,
    getTabsRevealOffset,
    getTabsOverflowMode,
    getTabsHiddenValues,
    getTabsScrollEdges,
    getTabsMenuLayout,
    getTabsThumbRect
} from "../../util";
import {
    getTabsThumbStyle,
    getTabsMarkStyle,
    getTabsMeasures
} from "../../stylesheet";
import {
    TabsListContext,
    withTabsContext
} from "../../context";
import EdgeFade from "../../../scrollArea/internal/edgeFade";
import TabsOverflowMenu from "../../internal/overflowMenu";
import TabsSlide from "../../internal/slide";

const TabsListContent: FC<TabsPartContentProps<ITabsListProps>> = ({
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    onLayout: onLayoutProp,
    accessibilityLabel,
    customFadeColor,
    children,
    context,
    style,
    title,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        duration: slideDuration,
        isReduceMotionEnabled,
        easing: slideEasing
    } = useMotionTiming("contentSwap", customTheme);

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isWeb = Platform.OS === "web";

    const isSegment = context.variant === "segment";

    const isVertical = context.orientation === "vertical";

    const overflowMode = getTabsOverflowMode({
        isOverflowAware: !isSegment && !isVertical && context.spreadBehaviour === "baseline",
        overflowBehaviour: context.overflowBehaviour,
        pointerType
    });

    const isScroller = overflowMode === "scroll";

    const isMenu = overflowMode === "menu";

    const scrollRef = useRef<ScrollView>(null);

    const layoutsRef = useRef<Map<string, LayoutRectangle>>(new Map());

    const previousValueRef = useRef<string | undefined>(context.value);

    const pendingRevealRef = useRef<string | undefined>(undefined);

    const isInitialRevealRef = useRef<boolean>(true);

    const rowWidthRef = useRef<number>(0);

    const previousHiddenKeyRef = useRef<string>("");

    const metricsRef = useRef<TabsScrollMetrics>({
        viewportWidth: 0,
        contentWidth: 0,
        offset: 0
    });

    const [
        slide,
        setSlide
    ] = useState<TabsSlideType | undefined>(undefined);

    const [
        scrollState,
        setScrollState
    ] = useState<TabsScrollState>({
        isOverflowing: false,
        isFadeStart: false,
        isMeasured: false,
        isFadeEnd: false
    });

    const [
        triggerWidths,
        setTriggerWidths
    ] = useState<Record<string, number>>({});

    const [
        moreWidth,
        setMoreWidth
    ] = useState<number | undefined>(undefined);

    const [
        barWidth,
        setBarWidth
    ] = useState<number>(0);

    const [
        isRevealForced,
        setIsRevealForced
    ] = useState<boolean>(false);

    const currentMeasures = getTabsMeasures({
        orientation: context.orientation,
        variant: context.variant,
        size: context.size,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        iconSizes,
        radiuses,
        spaces
    });

    const isInsetApplied = isScroller && scrollState.isOverflowing;

    const items = isMenu ? getTabsTriggerItems({
        children
    }) : [];

    const currentMoreWidth = moreWidth ?? currentMeasures.cellHeight;

    const moreGap = Math.max(0, currentMeasures.lineGap - (currentMoreWidth - currentMeasures.iconSize) / 2);

    const hiddenValues = getTabsHiddenValues({
        pinnedValue: items.some((item) => item.value === context.value) ? context.value : context.tabStopValue,
        gap: currentMeasures.lineGap,
        moreWidth: currentMoreWidth,
        availableWidth: barWidth,
        widths: triggerWidths,
        moreGap,
        items
    });

    const hiddenKey = JSON.stringify(hiddenValues);

    const isMenuMeasured = barWidth > 0 && items.every((item) => triggerWidths[item.value] !== undefined);

    const {
        scrollContent: scrollContentDynamicStyle,
        scroll: scrollDynamicStyle,
        frame: frameDynamicStyle,
        track: trackDynamicStyle,
        fade: fadeDynamicStyle,
        rail: railDynamicStyle,
        bar: barDynamicStyle,
        row: rowDynamicStyle
    } = useStyles({
        isOverflowVisible: isWeb && isScroller && scrollState.isMeasured && !isInsetApplied,
        isWidthControlled: StyleSheet.flatten<ViewStyle>(style)?.width !== undefined,
        isHidden: isMenu && !isMenuMeasured && !isRevealForced,
        spreadBehaviour: context.spreadBehaviour,
        isClipped: isMenu && !isMenuMeasured,
        orientation: context.orientation,
        variant: context.variant,
        userStyle: style,
        currentMeasures,
        isInsetApplied,
        shapeConfigs,
        isScroller,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        if(!isMenu || isRevealForced) {
            return;
        }

        const timeout = setTimeout(() => {
            setIsRevealForced(true);
        }, durations.short);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        isMenu
    ]);

    const handleScrollState = () => {
        const metrics = metricsRef.current;

        const edges = getTabsScrollEdges({
            direction,
            metrics,
            isWeb
        });

        const isMeasured = metrics.viewportWidth > 0 && rowWidthRef.current > 0;

        const isOverflowing = isMeasured && rowWidthRef.current > metrics.viewportWidth + TABS_SCROLL_EPSILON;

        setScrollState((current) => {
            if(current.isOverflowing === isOverflowing && current.isMeasured === isMeasured && current.isFadeStart === edges.isFadeStart && current.isFadeEnd === edges.isFadeEnd) {
                return current;
            }

            return {
                isOverflowing,
                isMeasured,
                ...edges
            };
        });
    };

    const handlePendingReveal = () => {
        const value = pendingRevealRef.current;

        if(!isScroller || value === undefined) {
            return;
        }

        const layout = layoutsRef.current.get(value);

        const metrics = metricsRef.current;

        if(!layout || !metrics.viewportWidth || !metrics.contentWidth) {
            return;
        }

        const isOverflowing = rowWidthRef.current > metrics.viewportWidth + TABS_SCROLL_EPSILON;

        const rowInset = isOverflowing ? spaces.spacingMd : 0;

        if(Math.abs(metrics.contentWidth - rowWidthRef.current - rowInset * 2) > TABS_SCROLL_EPSILON) {
            return;
        }

        pendingRevealRef.current = undefined;

        const offset = getTabsRevealOffset({
            fadeWidth: spaces.spacingXl,
            direction,
            rowInset,
            metrics,
            layout,
            isWeb
        });

        const isAnimated = !isInitialRevealRef.current && !isReduceMotionEnabled;

        isInitialRevealRef.current = false;

        if(offset === undefined) {
            return;
        }

        scrollRef.current?.scrollTo({
            animated: isAnimated,
            x: offset,
            y: 0
        });
    };

    const getTriggerLayout = (value: string) => {
        if(!isMenu) {
            return layoutsRef.current.get(value);
        }

        return getTabsMenuLayout({
            cellHeight: currentMeasures.cellHeight,
            cellInset: currentMeasures.cellInset,
            gap: currentMeasures.lineGap,
            widths: triggerWidths,
            hiddenValues,
            items,
            value
        });
    };

    useLayoutEffect(() => {
        const previousValue = previousValueRef.current;

        previousValueRef.current = context.value;

        pendingRevealRef.current = context.value;

        handlePendingReveal();

        if(previousValue === undefined || context.value === undefined || previousValue === context.value || !slideDuration || previousHiddenKeyRef.current !== hiddenKey) {
            setSlide(undefined);

            return;
        }

        const fromRect = getTabsThumbRect({
            layout: getTriggerLayout(previousValue),
            measures: currentMeasures
        });

        const toRect = getTabsThumbRect({
            layout: getTriggerLayout(context.value),
            measures: currentMeasures
        });

        if(!fromRect || !toRect) {
            setSlide(undefined);

            return;
        }

        setSlide({
            from: previousValue,
            to: context.value,
            fromRect,
            toRect
        });
    }, [
        context.value
    ]);

    useLayoutEffect(() => {
        previousHiddenKeyRef.current = hiddenKey;
    });

    const handleTriggerWidth = (value: string, layout: LayoutRectangle) => {
        if(!isMenu || layout.width <= 0) {
            return;
        }

        const width = layout.width - currentMeasures.cellInset * 2;

        setTriggerWidths((current) => {
            const currentWidth = current[value];

            if(currentWidth !== undefined && Math.abs(currentWidth - width) <= TABS_MEASURE_EPSILON) {
                return current;
            }

            return {
                ...current,
                [value]: width
            };
        });
    };

    const handleLayout = useStableCallback((value: string, layout: LayoutRectangle) => {
        layoutsRef.current.set(value, layout);

        handleTriggerWidth(value, layout);

        handlePendingReveal();
    });

    const handleReveal = useStableCallback((value: string) => {
        pendingRevealRef.current = value;

        handlePendingReveal();
    });

    const handleSlideEnd = (endedSlide: TabsSlideType) => {
        setSlide((current) => current === endedSlide ? undefined : current);
    };

    const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        metricsRef.current = {
            ...metricsRef.current,
            offset: event.nativeEvent.contentOffset.x
        };

        handleScrollState();
    };

    const onScrollLayout = (event: LayoutChangeEvent) => {
        metricsRef.current = {
            ...metricsRef.current,
            viewportWidth: event.nativeEvent.layout.width
        };

        handleScrollState();

        handlePendingReveal();
    };

    const onContentSizeChange = (width: number) => {
        metricsRef.current = {
            ...metricsRef.current,
            contentWidth: width
        };

        handleScrollState();

        handlePendingReveal();
    };

    const onBarLayout = (event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;

        if(width <= 0) {
            return;
        }

        setBarWidth((current) => Math.abs(current - width) > TABS_MEASURE_EPSILON ? width : current);
    };

    const onOverflowMenuLayout = (event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;

        if(width <= 0) {
            return;
        }

        setMoreWidth((current) => current !== undefined && Math.abs(current - width) <= TABS_MEASURE_EPSILON ? current : width);
    };

    const onRowLayout = (event: LayoutChangeEvent) => {
        rowWidthRef.current = event.nativeEvent.layout.width;

        handleScrollState();

        onLayoutProp?.(event);
    };

    const currentHiddenValues = useMemo(() => hiddenValues, [
        hiddenKey
    ]);

    const isFixed = isScroller || isMenu;

    const listContext = useMemo<TabsListContextType>(() => ({
        hiddenValues: currentHiddenValues,
        handleLayout,
        handleReveal,
        isFixed,
        slide
    }), [
        currentHiddenValues,
        handleLayout,
        handleReveal,
        isFixed,
        slide
    ]);

    const webProps: TabsListWebPropsType = isWeb ? {
        "aria-orientation": context.orientation
    } : {};

    const renderSlide = () => {
        if(!slide) {
            return null;
        }

        return <TabsSlide
            thumbStyle={isSegment ? getTabsThumbStyle({
                measures: currentMeasures,
                shapeConfigs,
                colors
            }) : {}}
            markStyle={getTabsMarkStyle({
                orientation: context.orientation,
                measures: currentMeasures,
                variant: context.variant,
                shapeConfigs,
                borders,
                colors
            })}
            onEnd={() => handleSlideEnd(slide)}
            key={`${slide.from}-${slide.to}`}
            orientation={context.orientation}
            variant={context.variant}
            duration={slideDuration}
            easing={slideEasing}
            slide={slide}
        />;
    };

    const renderBackdrop = () => {
        if(isSegment) {
            return <View
                style={[
                    stylesheet.layer,
                    stylesheet.span,
                    trackDynamicStyle
                ]}
                aria-hidden={true}
            />;
        }

        return <View
            style={[
                stylesheet.layer,
                isVertical ? stylesheet.railVertical : stylesheet.span,
                railDynamicStyle
            ]}
            aria-hidden={true}
        />;
    };

    const renderRow = () => {
        return <View
            {...props}
            {...webProps}
            aria-label={ariaLabel ?? accessibilityLabel ?? title}
            onLayout={isScroller ? onRowLayout : onLayoutProp}
            style={rowDynamicStyle}
            role={context.listRole}
        >
            {renderSlide()}
            {children}
        </View>;
    };

    const renderOverflowMenu = () => {
        if(!isMenu) {
            return null;
        }

        return <TabsOverflowMenu
            items={items.filter((item) => hiddenValues.includes(item.value))}
            customLocalize={context.customLocalize}
            isVisible={hiddenValues.length > 0}
            onLayout={onOverflowMenuLayout}
            onSelect={context.handleSelect}
            isDisabled={context.isDisabled}
            customTheme={customTheme}
            size={context.size}
            gap={moreGap}
        />;
    };

    const renderFade = (edge: "start" | "end", isVisible: boolean) => {
        if(!isVisible) {
            return null;
        }

        return <EdgeFade
            color={getTabsListFadeColor({
                customFadeColor,
                colors
            })}
            style={fadeDynamicStyle}
            edge={edge}
        />;
    };

    const renderContent = () => {
        if(isMenu) {
            return <View
                onLayout={onBarLayout}
                style={[
                    stylesheet.bar,
                    barDynamicStyle
                ]}
            >
                {renderRow()}
                {renderOverflowMenu()}
            </View>;
        }

        if(!isScroller) {
            return renderRow();
        }

        return <ScrollView
            contentContainerStyle={scrollContentDynamicStyle}
            onContentSizeChange={onContentSizeChange}
            showsHorizontalScrollIndicator={false}
            style={scrollDynamicStyle}
            onLayout={onScrollLayout}
            scrollEventThrottle={16}
            onScroll={onScroll}
            horizontal={true}
            ref={scrollRef}
        >
            {renderRow()}
        </ScrollView>;
    };

    return <TabsListContext.Provider
        value={listContext}
    >
        <View
            style={[
                style,
                frameDynamicStyle
            ]}
        >
            {renderBackdrop()}
            {renderContent()}
            {renderFade("start", scrollState.isFadeStart)}
            {renderFade("end", scrollState.isFadeEnd)}
        </View>
    </TabsListContext.Provider>;
};
export default withTabsContext("list", TabsListContent);
